import React from 'react';
import { Badge, Table } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { CountCell, MarginBadge } from './shared';
import { EarnedCell, PaidCell, useEarnedUnit, usePaidUnit } from './VisitCells';

/**
 * «به تفکیک مدت ضبط» — the visits by billed recording minutes (PM-0427).
 *
 * Every bucket of the service's closed list arrives, an empty one with
 * `visits = 0` and null means, so the table never changes shape between two
 * windows. Activating a bucket narrows the visit list to it (`?mins=`) — a
 * refetch, with the filter shown in the row above, never a hidden one.
 */
export default function VisitMinutesTable({ buckets, conv, active, onPick }) {
  const { t, formatNumber } = useTranslation();
  const earnedUnit = useEarnedUnit(conv);
  const paidUnit = usePaidUnit(conv);

  const bucketLabel = (b) => {
    if (b.bucket === 'none') return t('aiPricing.visits.minutes.none');
    if (b.bucket.endsWith('+')) return t('aiPricing.visits.minutes.atLeast', { min: formatNumber(Number(b.bucket.slice(0, -1))) });
    const [lo, hi] = b.bucket.split('-');
    return hi
      ? t('aiPricing.visits.minutes.range', { min: formatNumber(Number(lo)), max: formatNumber(Number(hi)) })
      : t('aiPricing.visits.minutes.exact', { count: Number(lo), value: formatNumber(Number(lo)) });
  };

  const columns = [
    {
      key: 'bucket',
      header: t('aiPricing.visits.minutes.columns.bucket'),
      render: (b) => (
        <span className="inline-flex items-center gap-1.5">
          <span className="font-semibold text-foreground">{bucketLabel(b)}</span>
          {active === b.bucket ? <Badge variant="outline">{t('aiPricing.visits.minutes.filtered')}</Badge> : null}
        </span>
      ),
    },
    { key: 'visits', header: t('aiPricing.visits.minutes.columns.visits'), align: 'end', width: '7rem', render: (b) => <CountCell value={b.visits} /> },
    {
      key: 'earned',
      header: t('aiPricing.visits.minutes.columns.earned', { unit: earnedUnit }),
      align: 'end',
      width: '9rem',
      render: (b) => <EarnedCell parseh={b.meanEarnedParseh} conv={conv} />,
    },
    {
      key: 'paid',
      header: t('aiPricing.visits.minutes.columns.paid', { unit: paidUnit }),
      align: 'end',
      width: '9rem',
      render: (b) => <PaidCell usd={b.meanPaidUsd} conv={conv} partial={b.visits > 0 && (b.pricedVisits ?? b.visits) < b.visits} />,
    },
    { key: 'margin', header: t('aiPricing.visits.minutes.columns.margin'), align: 'end', width: '6rem', render: (b) => <MarginBadge value={b.marginPct} /> },
  ];

  return (
    <Table
      dense
      ariaLabel={t('aiPricing.visits.minutes.aria')}
      columns={columns}
      rows={buckets || []}
      rowKey={(b) => b.bucket}
      onRowActivate={(b) => { if (b.visits > 0 || active === b.bucket) onPick(active === b.bucket ? '' : b.bucket); }}
      rowProps={(b) => ({
        'data-testid': `ai-visits-minutes-row-${b.bucket}`,
        'aria-label': t('aiPricing.visits.minutes.open', { bucket: bucketLabel(b) }),
        'aria-current': active === b.bucket ? 'true' : undefined,
      })}
      data-testid="ai-visits-minutes-table"
    />
  );
}
